<template>
    <f7-popup class="survey-flow" tablet-fullscreen>
        <div class="survey-flow__content" v-if="survey && step" :style="bgStyle">
            <div class="survey-flow__close" @click="abort"></div>

            <template v-if="step === 'intro'">
                <div class="survey-flow__tabs intro-tabs" :style="`grid-template-columns: repeat(${totalSteps}, 1fr);`">
                    <div class="survey-flow__tabs__item survey-flow__tabs__item__active"></div>
                    <template v-for="index in survey.questions.length">
                        <div :key="index" class="survey-flow__tabs__item"></div>
                    </template>
                    <div class="survey-flow__tabs__item"></div>
                </div>
                <div class="survey-flow__intro-buttons">
                    <button class="custom-button intro-button" @click="startQuestions">Пройти опрос</button>
                    <button class="custom-button skip-button" @click="skip">Пропустить</button>
                </div>
            </template>

            <template v-else-if="step === 'question'">
                <div class="survey-flow__tabs" :style="`grid-template-columns: repeat(${totalSteps}, 1fr);`">
                    <template v-for="index in totalSteps">
                        <div
                            :key="index"
                            class="survey-flow__tabs__item"
                            :class="{ 'survey-flow__tabs__item__active': index <= currentStepIndex }"
                        ></div>
                    </template>
                </div>
                <div class="survey-flow__title" :class="{ 'no-description': !activeQuestion.description }">{{ activeQuestion.title }}</div>
                <div class="survey-flow__description" v-if="activeQuestion.description">{{ activeQuestion.description }}</div>
                <div class="survey-flow__scrollable-content">
                    <div class="survey-flow__variants" v-if="activeQuestion.type === 'single' || activeQuestion.type === 'multiple'">
                        <div v-for="variant in activeQuestion.variants" :key="variant.id" class="survey-flow__variant">
                            <label>
                                <input
                                    :type="activeQuestion.type === 'single' ? 'radio' : 'checkbox'"
                                    :name="`q_${activeQuestion.id}`"
                                    :value="variant.id"
                                    :checked="isChecked(variant.id)"
                                    @change="toggleVariant(variant.id)"
                                />
                                <span>{{ variant.title }}</span>
                            </label>
                        </div>
                    </div>
                    <div v-else-if="activeQuestion.type === 'text'" class="survey-flow__text">
                        <textarea v-model="textAnswer" placeholder="Ваш ответ..."></textarea>
                    </div>
                </div>
                <div class="bottom-fixed-toolbar survey-fixed-toolbar center-button-survey">
                    <button
                        class="custom-button-two"
                        :disabled="!canProceed"
                        :style="continueButtonStyle"
                        @click="next"
                    >Продолжить</button>
                </div>
            </template>

            <template v-else-if="step === 'thankyou'">
                <div class="survey-flow__tabs thankyou-tabs" :style="`grid-template-columns: repeat(${totalSteps}, 1fr);`">
                    <template v-for="index in totalSteps">
                        <div :key="index" class="survey-flow__tabs__item survey-flow__tabs__item__active"></div>
                    </template>
                </div>
                <div class="survey-flow__title thankyou-title">Готово!<br/>Ваш промокод<br/>на бесплатную<br/>доставку 3 заказов</div>
                <div class="thankyou-content">
                    <div class="thankyou-card" v-if="reward && reward.code">
                        <img :src="cardImage" alt="Информация о промокоде" class="thankyou-card-image" @click="showCardImage" />
                        <div class="thankyou-card-clickable" @click="showCardImage"></div>
                    </div>
                    <div class="promocode-section" v-if="reward && reward.code">
                        <img :src="promocardImage" alt="Промокод" class="promocard-image" />
                        <div class="promocode-item thankyou-promocode">
                            <div class="promocode-item-content" @click="copyPromocode(reward.code)">
                                <p class="referer-code">{{ reward.code }}</p>
                                <button class="copy-button"></button>
                            </div>
                        </div>
                    </div>
                    <div class="center-button-survey">
                        <button class="custom-button-two" :style="buttonStyle(survey.thankyou_button_background)" @click="close">Спасибо!</button>
                    </div>
                </div>
            </template>
        </div>
        
        <!-- Модальное окно с промо -->
        <f7-popup class="promo-modal">
            <div class="promo-modal-content" @click="$f7.popup.close('.promo-modal')">
                <img :src="modalpromoImage" alt="Промо" class="promo-modal-image" @click="$f7.popup.close('.promo-modal')" />
            </div>
        </f7-popup>
    </f7-popup>
</template>

<script>
import { f7 } from 'framework7-vue';
import { mapState, mapGetters } from 'vuex';
import axios from '@/plugins/axios-silent';
import { getDeviceId, sendAppMetricaEvent } from '@/js/utils/service-helpers';

const AB_TEST_ONBOARDING_KEY = 'onboarding_3_0';
import cardImage from '../../static/card_txt.png';
import promocardImage from '../../static/promocard.png';
import modalpromoImage from '../../static/modalpromo.png';

export default {
    name: 'SurveyFlow',
    data: () => ({
        survey: null,
        step: null, // intro | question | thankyou
        activeIndex: 0,
        answers: {}, // { [questionId]: number[] }
        textAnswer: '',
        reward: null,
        cardImage,
        promocardImage,
        modalpromoImage,
        showPromoModal: false,
        showCardModal: false,
    }),
    computed: {
        ...mapState('auth', ['user']),
        ...mapGetters('remoteConfig', ['getAbTestConfig', 'getAbTestVariant', 'isInAbTest']),
        abConfig() {
            return this.getAbTestConfig(AB_TEST_ONBOARDING_KEY) || {};
        },
        abEventContext() {
            if (!this.isInAbTest(AB_TEST_ONBOARDING_KEY)) return {};
            return {
                ab_test_key: AB_TEST_ONBOARDING_KEY,
                ab_variant_key: this.getAbTestVariant(AB_TEST_ONBOARDING_KEY) || null
            };
        },
        activeQuestion() {
            if (!this.survey || !this.survey.questions) return {};
            return this.survey.questions[this.activeIndex] || {};
        },
        totalSteps() {
            if (this.isOnePerDay) return 1;
            return 1 + (this.survey?.questions?.length || 0) + 1;
        },
        isOnePerDay() {
            return this.survey?.type === 'one_per_day' && typeof this.survey?.current_question_index === 'number';
        },
        currentStepIndex() {
            if (this.step === 'intro') return 1;
            if (this.step === 'question') return 2 + this.activeIndex;
            if (this.step === 'thankyou') return this.totalSteps;
            return 1;
        },
        bgStyle() {
            let filename = '';
            if (this.step === 'intro') filename = this.survey?.intro_background || '';
            else if (this.step === 'question') filename = this.activeQuestion?.background || '';
            else if (this.step === 'thankyou') filename = this.survey?.thankyou_background || '';
            const url = filename ? `${process.env.VUE_APP_IMAGES_URL}/${filename}` : '';
            return url
                ? { backgroundImage: `url(${url})`, backgroundSize: 'cover', backgroundPosition: 'center center', backgroundRepeat: 'no-repeat' }
                : {};
        },
        canProceed() {
            if (this.isOnePerDay) return true;
            if (!this.activeQuestion || !this.activeQuestion.type) return true;
            if (this.activeQuestion.type === 'single' || this.activeQuestion.type === 'multiple') {
                const list = this.answers[this.activeQuestion.id] || [];
                return list.length > 0;
            }
            return true;
        },
        continueButtonStyle() {
            if (!this.canProceed) {
                return 'background:#1F3A6066;';
            }
            return this.buttonStyle(this.survey?.continue_button_background || this.survey?.intro_button_background);
        }
    },
    mounted() {
        this.$root.$on('surveyFlowStart', async (survey) => {
            if (!survey) return;
            const abConfig = this.getAbTestConfig(AB_TEST_ONBOARDING_KEY) || {};
            if (abConfig.survey === false && typeof abConfig.survey_id === 'number' && survey.id === abConfig.survey_id) {
                // Вместо простого закрытия — пробуем найти другой опрос
                try {
                    const { data } = await axios.get('/front/surveys');
                    const list = Array.isArray(data)
                        ? data.filter(item => item.id !== abConfig.survey_id)
                        : [];
                    if (list.length) {
                        // Запускаем следующий подходящий опрос
                        this.$root.$emit('surveyFlowStart', list[0]);
                    } else {
                        this.$root.$emit('surveyFlowClosed');
                    }
                } catch (e) {
                    this.$root.$emit('surveyFlowClosed');
                }
                return;
            }
            this.survey = survey;
            this.answers = {};
            const isOnePerDay = survey?.type === 'one_per_day' && typeof survey?.current_question_index === 'number';
            if (isOnePerDay) {
                this.step = 'question';
                this.activeIndex = survey.current_question_index;
                sendAppMetricaEvent('survey_question_shown', {
                    question_id: `question_${this.activeIndex + 1}`,
                    step: 1,
                    total_steps: 1,
                    ...this.abEventContext
                });
            } else {
                this.step = 'intro';
                this.activeIndex = 0;
                sendAppMetricaEvent('survey_intro_shown', this.abEventContext);
            }
            this.$f7.popup.open('.survey-flow');
        });
    },
    methods: {
        bgImageStyle(filename) {
            const url = filename ? `${process.env.VUE_APP_IMAGES_URL}/${filename}` : '';
            return url ? { backgroundImage: `url(${url})` } : {};
        },
        buttonStyle(color) {
            return color ? `background:${color};` : '';
        },
        async skip() {
            sendAppMetricaEvent('survey_intro_chosen', {
                action: 'skip_survey',
                ...this.abEventContext
            });
            this.$f7.popup.close('.survey-flow');
            this.$root.$emit('surveyFlowClosed');
        },
        startQuestions() {
            sendAppMetricaEvent('survey_intro_chosen', {
                action: 'start_survey',
                ...this.abEventContext
            });
            this.step = 'question';
            // Отправляем событие показа первого вопроса
            this.sendQuestionShownEvent();
        },
        isChecked(variantId) {
            const qid = this.activeQuestion.id;
            const list = this.answers[qid] || [];
            return list.includes(variantId);
        },
        toggleVariant(variantId) {
            const qid = this.activeQuestion.id;
            const type = this.activeQuestion.type;
            const prev = this.answers[qid] ? [...this.answers[qid]] : [];
            if (type === 'single') {
                this.$set(this.answers, qid, [variantId]);
            } else {
                const idx = prev.indexOf(variantId);
                if (idx >= 0) prev.splice(idx, 1); else prev.push(variantId);
                this.$set(this.answers, qid, prev);
            }
        },
        async next() {
            // persist text answer if needed
            if (this.activeQuestion.type === 'text') {
                const qid = this.activeQuestion.id;
                this.$set(this.answers, qid, this.textAnswer ? [this.textAnswer] : []);
            }
            this.sendQuestionChosenEvent();

            if (this.isOnePerDay) {
                await this.complete();
                return;
            }
            if (this.activeIndex < this.survey.questions.length - 1) {
                this.activeIndex += 1;
                this.textAnswer = '';
                this.sendQuestionShownEvent();
            } else {
                await this.complete();
            }
        },
        async abort() {
            // Отправляем событие закрытия опроса в зависимости от текущего шага
            if (this.step === 'intro') {
                sendAppMetricaEvent('survey_intro_chosen', {
                    action: 'close_survey',
                    ...this.abEventContext
                });
            } else if (this.step === 'question') {
                // Отправляем событие прерывания на этапе вопросов
                const questionIds = [
                    'survey_living',
                    'survey_tower', 
                    'survey_source',
                    'survey_task',
                    'survey_target'
                ];
                const questionId = questionIds[this.activeIndex] || `question_${this.activeIndex + 1}`;
                
                sendAppMetricaEvent('survey_question_aborted', {
                    question_id: questionId,
                    step: this.activeIndex + 1,
                    total_steps: this.survey.questions.length,
                    completed_questions: this.activeIndex,
                    ...this.abEventContext
                });
            }
            
            try {
                const device_id = await getDeviceId();
                const user_id = this.user?.id || null;
                let partial_results = [];
                if (this.isOnePerDay && this.activeQuestion?.id) {
                    const q = this.activeQuestion;
                    if (q.type === 'text') {
                        partial_results = [{ survey_question_id: q.id, type: q.type, comment: (this.answers[q.id] || [])[0] || null }];
                    } else {
                        const variant_ids = this.answers[q.id] || [];
                        partial_results = [{ survey_question_id: q.id, type: q.type, variant_ids }];
                    }
                } else {
                    const till = Math.min(this.activeIndex, this.survey.questions.length - 1);
                    for (let i = 0; i <= till; i++) {
                        const q = this.survey.questions[i];
                        if (q.type === 'text') {
                            const comment = (this.answers[q.id] || [])[0] || '';
                            if (comment) partial_results.push({ survey_question_id: q.id, type: q.type, comment });
                        } else {
                            const variant_ids = (this.answers[q.id] || []);
                            if (variant_ids.length) partial_results.push({ survey_question_id: q.id, type: q.type, variant_ids });
                        }
                    }
                }
                axios.post(`/front/surveys/${this.survey.id}/abort`, { 
                    partial_results, 
                    device_id, 
                    user_id 
                });
            } catch (e) {}
            this.$f7.popup.close('.survey-flow');
            this.$root.$emit('surveyFlowClosed');
        },
        async complete() {
            const device_id = await getDeviceId();
            const user_id = this.user?.id || null;
            let results = [];
            if (this.isOnePerDay && this.activeQuestion?.id) {
                const q = this.activeQuestion;
                if (q.type === 'text') {
                    results = [{ survey_question_id: q.id, type: q.type, comment: (this.answers[q.id] || [])[0] || '' }];
                } else {
                    results = [{ survey_question_id: q.id, type: q.type, variant_ids: this.answers[q.id] || [] }];
                }
            } else {
                for (const q of this.survey.questions) {
                    if (q.type === 'text') {
                        const comment = (this.answers[q.id] || [])[0] || '';
                        results.push({ survey_question_id: q.id, type: q.type, comment });
                    } else {
                        const variant_ids = (this.answers[q.id] || []);
                        results.push({ survey_question_id: q.id, type: q.type, variant_ids });
                    }
                }
            }
            try {
                const { data } = await axios.post(`/front/surveys/${this.survey.id}/complete`, {
                    results,
                    device_id,
                    user_id
                });
                if (this.isInAbTest(AB_TEST_ONBOARDING_KEY)) {
                    this.$store.dispatch('remoteConfig/trackAbTestConversion', {
                        testKey: AB_TEST_ONBOARDING_KEY,
                        eventData: { survey_id: this.survey.id, completed: true }
                    });
                }
                sendAppMetricaEvent('survey_completed', {
                    survey_id: this.survey.id,
                    ...this.abEventContext
                });
                if (!this.isOnePerDay) {
                    this.step = 'thankyou';
                    this.reward = data && data.reward ? data.reward : null;
                } else {
                    this.$f7.popup.close('.survey-flow');
                    this.$root.$emit('surveyFlowClosed');
                }
            } catch (e) {
                if (!this.isOnePerDay) this.step = 'thankyou';
                else {
                    this.$f7.popup.close('.survey-flow');
                    this.$root.$emit('surveyFlowClosed');
                }
            }
        },
        close() {
            this.$f7.popup.close('.survey-flow');
            this.$root.$emit('surveyFlowClosed');
            // После завершения опроса перенаправляем на авторизацию, если не авторизован
            if (!this.user || !this.user.phone) {
                try {
                    this.$f7.views && this.$f7.views[0] && this.$f7.views[0].router && this.$f7.views[0].router.navigate('/login');
                } catch (e) {}
            }
        },
        toPromocodes() {
            this.$f7.popup.close('.survey-flow');
            this.$root.$emit('surveyFlowClosed');
            f7.views[0].router.navigate('/referal-program');
        },
        async copyPromocode(code) {
            if (!code) return;
            try {
                if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(code);
                    this.$f7.dialog.alert('Скопировано');
                } else if (this.$copyText) {
                    await this.$copyText(code);
                    this.$f7.dialog.alert('Скопировано');
                }
            } catch (e) {}
        },
        showPromoRules() {
            this.showPromoModal = true;
        },
        showCardImage() {
            this.$f7.popup.open('.promo-modal');
        },
        sendQuestionShownEvent() {
            if (!this.activeQuestion || !this.survey) return;
            
            // Определяем question_id на основе типа вопроса или индекса
            const questionIds = [
                'survey_living',
                'survey_tower', 
                'survey_source',
                'survey_task',
                'survey_target'
            ];
            
            const questionId = questionIds[this.activeIndex] || `question_${this.activeIndex + 1}`;
            
            sendAppMetricaEvent('survey_question_shown', {
                question_id: questionId,
                step: this.activeIndex + 1,
                total_steps: this.survey.questions.length,
                ...this.abEventContext
            });
        },
        sendQuestionChosenEvent() {
            if (!this.activeQuestion || !this.survey) return;
            
            const qid = this.activeQuestion.id;
            const answers = this.answers[qid] || [];
            
            // Для текстовых вопросов берем текст
            let answerValue;
            if (this.activeQuestion.type === 'text') {
                answerValue = this.textAnswer || '';
            } else {
                // Для вариантов ответа нужно получить значения вариантов
                const selectedVariants = this.activeQuestion.variants?.filter(variant => 
                    answers.includes(variant.id)
                ).map(variant => variant.title) || [];
                
                answerValue = this.activeQuestion.type === 'multiple' 
                    ? selectedVariants 
                    : selectedVariants[0] || '';
            }
            
            sendAppMetricaEvent('survey_question_chosen', {
                answer_value: answerValue,
                is_multiple: this.activeQuestion.type === 'multiple',
                ...this.abEventContext
            });
        }
    }
};
</script>

<style lang="scss">
.survey-flow {
    z-index: 99998;
    height: 100%;
    &__content { position: relative; height: 100%; padding: 25px; padding-bottom: 120px; display: flex; flex-direction: column; }
    &__close { position: absolute; top: calc(env(safe-area-inset-top) + 36px); right: 16px; width: 32px; height: 32px; background: url('../../static/survey/close.svg') center/contain no-repeat; }
    &__image { width: calc(100% + 48px); height: 240px; margin: 0 -24px 16px; background: #f5f5f5 center/cover no-repeat; border-radius: 0; }
    &__buttons { display: grid; grid-gap: 8px; }
    &__intro-buttons { 
        position: fixed; 
        left: 16px; 
        right: 16px; 
        bottom: calc(63px + env(safe-area-inset-bottom)); 
        display: flex; 
        flex-direction: column; 
        gap: 8px; 
    }
    .custom-button { height: 48px; border-radius: 36px; color: #fff; background: #FF7E8A; }
    .custom-button-two { height: 48px; border-radius: 36px; color: #fff; background: #FF7E8A; margin-top: 25px; }
    .intro-button { background: #1d3572; color: #fff; }
    .skip-button { background: #a0acbf; color: #fff; }
    &__progress { color: #B8B8B8; font-size: 14px; margin-bottom: 8px; }
    &__title { 
        color: #1F3A60; 
        font-weight: 700; 
        font-size: 28px; 
        line-height: 33px; 
        letter-spacing: 0; 
        margin-top: 30px; 
        margin-bottom: 12px;
        &.no-description {
            margin-bottom: 32px;
        }
    }
    &__variants { display: grid; grid-gap: 20px; margin-bottom: 16px; }
    &__variant { label { display:flex; align-items:center; gap:12px; } }
    &__variant label span { font-weight: 400; font-size: 14px; line-height: 100%; letter-spacing: 0; }
    &__more { color: #007aff; font-size: 14px; margin-bottom: 10px; cursor: pointer; align-self: flex-start; }
    &__tabs { 
        display: grid; 
        grid-gap: 6px; 
        height: 6px; 
        padding-top: calc(env(safe-area-inset-top) + 26px); 
        margin-bottom: 8px; 
        margin-right: 30px; 
    }
    &__tabs__item { width: 100%; height: 4px; background: #DFE0E3; opacity: 1; border-radius: 10px; }
    &__tabs__item__active { background: #1F3A5F; opacity: 1; }
    &__description { 
        color: #1F3A60; 
        font-size: 18px; 
        font-weight: 500; 
        margin-bottom: 32px; 
    }
    &__scrollable-content { 
        flex: 1; 
        overflow-y: auto; 
        // padding-top: 30px;
        padding-bottom: 20px; 
        margin-bottom: calc(48px + 0px + env(safe-area-inset-bottom) + 32px);
        padding: 0px 22px 0px 16px !important;
    }
    .thankyou-title { 
        text-align: center; 
        margin-bottom: 30px; 
    }
    .thankyou-content {
        flex: 1;
        padding-bottom: 39px; 
        margin-bottom: calc(48px + 0px + env(safe-area-inset-bottom) + 32px);
    }
    .thankyou-card {
        position: relative;
        margin-bottom: 19px;
    }
    .thankyou-card-image {
        width: 100%;
        height: auto;
        display: block;
        margin: 0 auto 19px auto;
        max-width: 303px;   
        max-height: 248px;
        cursor: pointer;
    }
    .thankyou-card-clickable {
        position: absolute;
        bottom: 19px;
        left: 50%;
        transform: translateX(-50%);
        width: 52px;
        height: 33.33%;
        cursor: pointer;
        z-index: 10;
    }

    .promocode-section {
        position: relative;
        margin-bottom: 20px;
    }
    .promocard-image {
        width: 100%;
        max-width: 303px;
        height: auto;
        display: block;
        position: relative;
        z-index: 1;
        margin: 0 auto;
    }
    .thankyou-promocode {
        position: relative;
        z-index: 2;
        margin: -10% auto 0 auto;
        width: 100%;
        max-width: 303px;
    }
    @media (min-width: 768px) {
        .thankyou-content {
            padding-bottom: 120px;
            margin-bottom: calc(120px + env(safe-area-inset-bottom));
        }
    }
}
/* bottom fixed toolbar centered with 16px sides and min 63px from bottom */
.survey-fixed-toolbar { 
    position: fixed; 
    left: 0; 
    right: 16px; 
    // bottom: calc(63px + env(safe-area-inset-bottom));
    // bottom: calc(63px + constant(safe-area-inset-bottom)); 
    bottom: 63px;
    display: flex; justify-content: center; 
}
.survey-fixed-toolbar .custom-button { width: 100%; max-width: 343px; }
// @media (min-width: 768px) {
//     .survey-fixed-toolbar { 
//         bottom: calc(24px + env(safe-area-inset-bottom)); 
//         bottom: calc(24px + constant(safe-area-inset-bottom)); 
//     }
// }
/* custom inputs styling */
.survey-flow input[type='radio'], .survey-flow input[type='checkbox'] { width: 24px; height: 24px; box-sizing: border-box; appearance: none; outline: none; display: inline-block; min-width: 24px; min-height: 24px; }
/* checkbox: radius 6px, thinner tick */
.survey-flow input[type='checkbox'] { border: 2px solid #1F3A60; border-radius: 6px; background: transparent; position: relative; }
.survey-flow input[type='checkbox']:checked { background: #1F3A60; }
.survey-flow input[type='checkbox']:checked::after { content: ''; position: absolute; left: 7px; top: 4px; width: 6px; height: 10px; border-right: 2px solid #fff; border-bottom: 2px solid #fff; transform: rotate(45deg); }
/* radio: ring fill on sides, hollow center */
.survey-flow input[type='radio'] { border: 2px solid #1F3A60; border-radius: 50%; background: transparent; }
.survey-flow input[type='radio']:checked { background: radial-gradient(circle, transparent 33%, #1F3A60 40%); }

.promocode-item { background-color: #f0dfc5; color: #484848; border-radius: 16px; padding: 6px 6px; margin-bottom: 16px; }
.promocode-item-content { display: flex; align-items: center; padding: 0px 12px; border: 1px dashed #484848; border-radius: 10px; cursor: pointer; }
.referer-code { font-size: 14px; font-weight: 700; color: #000000; }
.copy-button { margin-left: auto; width: 24px; height: 24px; background: url('../../static/copyBtn.svg') no-repeat 50%; }
.center-button-survey { 
    display: flex;
    justify-content: center;
    align-items: center;
}

/* Модальное окно промо */
.promo-modal {
    background: rgba(0, 0, 0, 0.5);
    z-index: 99999;
}
.promo-modal-content {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
}
.promo-modal-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    cursor: pointer;
    max-width: 334px;
    max-height: 432px;
}
</style>


